import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/company/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { useCompanyRole } from '@/hooks/useCompanyRole';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle, FileText, Landmark, Upload, Download, CheckCircle, Clock, XCircle } from 'lucide-react';
import { ReceiptUploadDialog } from '@/components/company/ReceiptUploadDialog';
import { InvoiceStatusBadge } from '@/components/insurance/InvoiceStatusBadge';
import { InvoiceLineItems } from '@/components/insurance/InvoiceLineItems';
import type { IPaymentReceipt } from '@/@types/payment';

// ============================================================================
// Shared helpers
// ============================================================================

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const receiptStatusConfig: Record<string, { icon: typeof Clock; className: string; label: string }> = {
  pending_review: { icon: Clock, className: 'text-yellow-500', label: 'Pending Review' },
  approved: { icon: CheckCircle, className: 'text-green-500', label: 'Approved' },
  rejected: { icon: XCircle, className: 'text-red-500', label: 'Rejected' },
};

// ============================================================================
// Sub-components
// ============================================================================

interface IInvoiceInfoCardProps {
  dueDate?: number;
  createdAt: number;
  currency: string;
}

function InvoiceInfoCard({ dueDate, createdAt, currency }: IInvoiceInfoCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <div className="rounded-lg bg-primary/10 p-2">
            <FileText className="w-4 h-4 text-primary" />
          </div>
          Invoice Details
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4 text-sm">
        {dueDate && (
          <div>
            <span className="text-muted-foreground">Due Date: </span>
            <span className="text-foreground">{formatDate(dueDate)}</span>
          </div>
        )}
        <div>
          <span className="text-muted-foreground">Created: </span>
          <span className="text-foreground">{formatDate(createdAt)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Currency: </span>
          <span className="text-foreground">{currency}</span>
        </div>
      </CardContent>
    </Card>
  );
}

interface IInvoiceLineItemsCardProps {
  lineItems: Array<{ policyId: Id<'insurancePolicies'>; description: string; amount: number }>;
  currency: string;
  subtotal: number;
  tax?: number;
  total: number;
}

function InvoiceLineItemsCard({ lineItems, currency, subtotal, tax, total }: IInvoiceLineItemsCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Line Items</CardTitle>
      </CardHeader>
      <CardContent>
        <InvoiceLineItems
          lineItems={lineItems}
          currency={currency}
          subtotal={subtotal}
          tax={tax}
          total={total}
        />
      </CardContent>
    </Card>
  );
}

interface IInvoicePaymentCardProps {
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  iban?: string;
  swiftCode?: string;
}

function InvoicePaymentCard({ bankName, accountHolderName, accountNumber, iban, swiftCode }: IInvoicePaymentCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="w-4 h-4 text-muted-foreground" />
          Payment Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>
          <span className="text-muted-foreground">Bank: </span>
          <span className="text-foreground">{bankName}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Account Holder: </span>
          <span className="text-foreground">{accountHolderName}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Account: </span>
          <span className="text-foreground font-mono">{accountNumber}</span>
        </div>
        {iban && (
          <div>
            <span className="text-muted-foreground">IBAN: </span>
            <span className="text-foreground font-mono">{iban}</span>
          </div>
        )}
        {swiftCode && (
          <div>
            <span className="text-muted-foreground">SWIFT: </span>
            <span className="text-foreground font-mono">{swiftCode}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface IInvoiceReceiptsCardProps {
  receipts: IPaymentReceipt[];
  canUpload: boolean;
  onUploadClick: () => void;
}

function InvoiceReceiptsCard({ receipts, canUpload, onUploadClick }: IInvoiceReceiptsCardProps) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Receipts ({receipts.length})</CardTitle>
          {canUpload && (
            <Button variant="outline" size="sm" onClick={onUploadClick}>
              <Upload aria-hidden="true" className="w-3.5 h-3.5 mr-1.5" />
              Upload
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {receipts.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No receipts uploaded yet.
          </p>
        )}
        <div className="space-y-3">
          {receipts.map(receipt => {
            const config = receiptStatusConfig[receipt.status] ?? receiptStatusConfig['pending_review'];
            const StatusIcon = config.icon;
            return (
              <div
                key={receipt._id}
                className="flex items-start gap-3 p-3 border border-border rounded-lg"
              >
                <StatusIcon className={`w-4 h-4 mt-0.5 shrink-0 ${config.className}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{receipt.fileName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(receipt.createdAt)} — {config.label}
                  </p>
                  {receipt.aiAnalysis && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      AI: {receipt.aiAnalysis}
                    </p>
                  )}
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                  <Download className="w-3.5 h-3.5" />
                  <span className="sr-only">Download {receipt.fileName}</span>
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Main page
// ============================================================================

export function InvoiceDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canViewInvoices, canUploadReceipts } = useCompanyRole();
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const invoiceId = id as Id<'companyInvoices'>; // AS_JUSTIFIED: one-time Id cast from route param

  const invoice = useQuery(
    api.invoices.queries.get,
    id ? { invoiceId } : 'skip'
  );
  const receipts = useQuery(
    api.payments.queries.listByInvoice,
    id ? { invoiceId } : 'skip'
  );
  const bankAccount = useQuery(
    api.bankAccounts.queries.get,
    invoice?.bankAccountId ? { accountId: invoice.bankAccountId } : 'skip'
  );

  const isLoading = invoice === undefined || receipts === undefined;

  const handleBack = useCallback(() => {
    navigate('/company/invoices');
  }, [navigate]);

  if (!canViewInvoices) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Access Denied</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Only admin and accountant roles can view invoices.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-full bg-background-secondary p-8">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-7 w-48" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-full bg-background-secondary flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Invoice not found</h2>
          <p className="text-sm text-muted-foreground mb-4">
            This invoice may not exist or you don&apos;t have access.
          </p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary p-8">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-xl font-semibold text-foreground">
          Invoice {invoice.invoiceNumber}
        </h1>
        <InvoiceStatusBadge status={invoice.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <InvoiceInfoCard
            dueDate={invoice.dueDate}
            createdAt={invoice.createdAt}
            currency={invoice.currency}
          />
          <InvoiceLineItemsCard
            lineItems={invoice.lineItems}
            currency={invoice.currency}
            subtotal={invoice.subtotal}
            tax={invoice.tax}
            total={invoice.total}
          />
        </div>

        <div className="space-y-6">
          {bankAccount && (
            <InvoicePaymentCard
              bankName={bankAccount.bankName}
              accountHolderName={bankAccount.accountHolderName}
              accountNumber={bankAccount.accountNumber}
              iban={bankAccount.iban}
              swiftCode={bankAccount.swiftCode}
            />
          )}
          <InvoiceReceiptsCard
            receipts={receipts}
            canUpload={canUploadReceipts}
            onUploadClick={() => setIsUploadOpen(true)}
          />
        </div>
      </div>

      <ReceiptUploadDialog
        open={isUploadOpen}
        onOpenChange={setIsUploadOpen}
        invoiceId={invoice._id}
        companyId={invoice.companyId}
      />
    </div>
  );
}
